import { makeWidgetsList, ModalWindow, ToastBar } from '@libs/core';
import { PortalProvider } from '@libs/hooks';
import { selectBottomSheets, selectModalWindows, selectStatus, useAppSelector } from '@libs/store';
import { EStatus } from '@libs/types';
import { Portal } from '@libs/utils';
import React, { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { PageWrapper } from './PageWrapper';
import BottomSheet from './popup/BottomSheet';
import Popup from './popup/Popup';
import Widget from '../Widget';

const DELAY = 1000;

const WidgetsList = makeWidgetsList(Widget);

export const PortalWrapper = ({ children }: { children: React.ReactNode }) => {
    const bottomSheets = useAppSelector(selectBottomSheets);
    const modalWindows = useAppSelector(selectModalWindows);
    const status = useAppSelector(selectStatus);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const startFetchTime = useRef<number | null>(null);
    const bsInternalIdForOpen = useMemo(() => Object.keys(bottomSheets).find((bsInternalId) => bottomSheets[bsInternalId].isOpen), [bottomSheets]);
    const [modal, setModal] = useState<React.ReactNode>(undefined);
    const [bottomSheet, setBottomSheet] = useState<string | undefined>();
    const [modalWindow, setModalWindow] = useState<string | undefined>();
    const [popup, setPopup] = useState<React.ReactNode>(undefined);

    const contextValue = useMemo(
        () => ({
            setModal,
            setPopup,
            setBottomSheet,
            setModalWindow,
        }),
        [],
    );

    const updateBottomSheet = useEffectEvent((status?: EStatus, bsInternalIdForOpen?: string) => {
        if (status === EStatus.Loading) {
            setBottomSheet(undefined);
            startFetchTime.current = Date.now();
        } else if (bsInternalIdForOpen) {
            const delta = Date.now() - (startFetchTime.current ?? 0);
            if (delta < DELAY) {
                timeoutRef.current = setTimeout(() => {
                    setBottomSheet(bsInternalIdForOpen);
                }, DELAY - delta);
            } else {
                setBottomSheet(bsInternalIdForOpen);
            }
        }
    });

    useEffect(() => {
        updateBottomSheet(status, bsInternalIdForOpen);
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [bsInternalIdForOpen, status]);

    return (
        <PageWrapper>
            <PortalProvider value={contextValue}>
                {children}
                <BottomSheet
                    widgetId={bottomSheet}
                    status={status}
                    modal={modal}
                    setBottomSheet={setBottomSheet}
                    bottomSheets={bottomSheets}
                    WidgetsList={WidgetsList}
                />
                <ModalWindow widgetId={modalWindow} status={status} modalWindows={modalWindows} WidgetsList={WidgetsList} setModalWindow={setModalWindow} />
                <Popup content={popup} modal={modal} setPopup={setPopup} />
                <ToastBar />
                {modal && <Portal>{modal}</Portal>}
            </PortalProvider>
        </PageWrapper>
    );
};
